import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import AdminService from '../../../services/adminService';
import PathwayForm from '../../../components/admin/PathwayForm';
import ScoringRulesManager from '../../../components/admin/ScoringRulesManager';
import PathwayCard from '../../../components/admin/PathwayCard';
import PathwayVersionsModal from '../../../components/admin/PathwayVersionsModal';

import { useAuth } from '../../../context/AuthContext';

/**
 * Pathway Manager Component
 *
 * Provides comprehensive management for immigration pathways:
 * - Manage pathways for each country
 * - Configure cost components
 * - Set up scoring criteria and rules
 * - Visual pathway representation
 */
const PathwayManager = () => {
  const { t } = useLanguage();
  const { profile } = useAuth();
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPathway, setEditingPathway] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [versionsPathway, setVersionsPathway] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadPathways(selectedCountry.id);
    } else {
      setPathways([]);
    }
  }, [selectedCountry]);

  const loadCountries = async () => {
    try {
      const data = await AdminService.getCountries();
      setCountries(data);
      if (data.length > 0 && !selectedCountry) {
        setSelectedCountry(data[0]);
      }
    } catch (err) {
      console.error('Error loading countries:', err);
      setError(t('admin.pathways.error.load_countries', 'Failed to load countries'));
    } finally {
      setLoading(false);
    }
  };

  const loadPathways = async (countryId) => {
    try {
      const data = await AdminService.getImmigrationPathways(countryId);
      setPathways(data);
      setSelectedIds([]);
      setError('');
      setInfo('');
    } catch (err) {
      console.error('Error loading pathways:', err);
      setError(t('admin.pathways.error.load_pathways', 'Failed to load pathways'));
    }
  };

  const toggleSelect = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
  };

  const selectAll = (checked) => {
    if (checked) {
      setSelectedIds(pathways.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkSetActive = async (isActive) => {
    if (selectedIds.length === 0) return;
    try {
      setBulkBusy(true);
      await AdminService.bulkSetPathwaysActive(selectedIds, isActive);
      await loadPathways(selectedCountry.id);
    } catch (err) {
      console.error('Error bulk updating pathways:', err);
      setError('Failed to update pathways state.');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleCreate = () => {
    setEditingPathway(null);
    setShowForm(true);
  };

  const handleEdit = (pathway) => {
    setEditingPathway(pathway);
    setShowForm(true);
  };

  const handleDelete = async (pathwayId) => {
    if (!window.confirm(t('admin.pathways.confirm_delete', 'Are you sure you want to delete this pathway?'))) {
      return;
    }

    try {
      await AdminService.deletePathway(pathwayId);
      await loadPathways(selectedCountry.id);
    } catch (err) {
      console.error('Error deleting pathway:', err);
      setError(t('admin.pathways.error.delete', 'Failed to delete pathway'));
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      const pathwayData = {
        ...formData,
        country_id: selectedCountry.id
      };

      if (editingPathway) {
        await AdminService.updatePathway(editingPathway.id, pathwayData);
      } else {
        await AdminService.createPathway(pathwayData);
      }

      setShowForm(false);
      setEditingPathway(null);
      await loadPathways(selectedCountry.id);
    } catch (err) {
      console.error('Error saving pathway:', err);
      setError(t('admin.pathways.error.save', 'Failed to save pathway'));
    }
  };

  const buildSnapshot = (pathway) => {
    const { current_version, countries: countryMeta, ...rest } = pathway;
    if (current_version?.content) {
      return { ...current_version.content };
    }
    const clone = { ...rest };
    delete clone.countries;
    delete clone.current_version;
    return clone;
  };

  const handleCreateDraft = async (pathway) => {
    try {
      setError('');
      setInfo('');
      const snapshot = buildSnapshot(pathway);
      const changeNotes = `Draft cloned from version ${pathway.current_version?.version_number || 1}`;
      await AdminService.createPathwayDraft(pathway.id, {
        authorId: profile?.id || null,
        changeNotes,
        baseContent: snapshot
      });
      setInfo('Draft version created. Version editor coming soon.');
    } catch (err) {
      console.error('Error creating draft version:', err);
      setError('Failed to create draft version.');
    }
  };

  const handleOpenVersions = (pathway) => {
    setVersionsPathway(pathway);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p className="mt-2 text-gray-600">Loading pathways...</p>
      </div>
    );
  }

  return (
    <div className="admin-page text-white font-mono min-h-screen bg-gray-900">
      {/* Header */}
      <div className="admin-header">
        <h2 className="admin-title">
          🛤️ Immigration Pathways
        </h2>
        <p className="admin-subtitle">
          {t('admin.pathways.subtitle', 'Manage immigration pathways, costs, and scoring criteria')}
        </p>
      </div>

      {/* Country Selector */}
      {countries.length > 0 && (
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-content">
            <label className="block text-sm font-semibold text-white mb-4">
              {t('admin.pathways.select_country', 'Select Country')}
            </label>
            <div className="admin-grid-3">
              {countries.map((country) => (
                <button
                  key={country.id}
                  onClick={() => setSelectedCountry(country)}
                  className={`p-4 border-2 rounded text-center ${selectedCountry?.id === country.id
                    ? 'border-blue-500 bg-blue-900/30'
                    : 'border-gray-600 hover:border-blue-400 bg-gray-700'
                    }`}
                >
                  <div className="flex flex-col items-center">
                    <span className="text-3xl mb-2">{country.flag_emoji}</span>
                    <div>
                      <div className="font-semibold text-sm text-white">{country.name}</div>
                      <div className="text-xs text-gray-400 mt-1">{country.code}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {(error || info) && (
        <div className={`admin-alert ${error ? 'admin-alert-error' : 'admin-alert-success'}`}>
          <p>{error || info}</p>
        </div>
      )}

      {/* Pathways for Selected Country */}
      {selectedCountry && (
        <div className="admin-card bg-gray-800 border-gray-700">
          <div className="admin-card-header border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-3xl">{selectedCountry.flag_emoji}</span>
                <div>
                  <h3 className="text-lg font-medium text-white">
                    {selectedCountry.name} {t('admin.pathways.pathways_title', 'Pathways')}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {pathways.length} {t('admin.pathways.total_pathways', 'pathways')} configured
                  </p>
                </div>
              </div>
              <div className="admin-actions">
                <label className="text-xs text-gray-400 flex items-center gap-1">
                  <input type="checkbox" onChange={(e) => selectAll(e.target.checked)} checked={selectedIds.length > 0 && selectedIds.length === pathways.length} />
                  Select all
                </label>
                <button
                  onClick={() => handleBulkSetActive(true)}
                  disabled={bulkBusy || selectedIds.length === 0}
                  className="admin-btn admin-btn-success admin-btn-sm disabled:opacity-50"
                >
                  Activate
                </button>
                <button
                  onClick={() => handleBulkSetActive(false)}
                  disabled={bulkBusy || selectedIds.length === 0}
                  className="admin-btn admin-btn-danger admin-btn-sm disabled:opacity-50"
                >
                  Deactivate
                </button>
                <button onClick={handleCreate} className="admin-btn admin-btn-primary admin-btn-sm">
                  ➕ {t('admin.pathways.add_new', 'Add Pathway')}
                </button>
              </div>
            </div>
          </div>

          {pathways.length === 0 ? (
            <div className="admin-card-content text-center">
              <div className="text-6xl mb-4">🛤️</div>
              <h3 className="text-lg font-medium text-white mb-2">
                {t('admin.pathways.no_pathways', 'No pathways yet')}
              </h3>
              <p className="text-gray-400 mb-4">
                {t('admin.pathways.add_first_pathway', 'Add your first immigration pathway for this country')}
              </p>
              <button onClick={handleCreate} className="admin-btn admin-btn-primary admin-btn-sm">
                ➕ {t('admin.pathways.add_first_button', 'Add First Pathway')}
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {pathways.map((pathway) => (
                <div key={pathway.id} className="p-4">
                  <div className="mb-4">
                    <label className="text-xs text-gray-400 flex items-center gap-2">
                      <input type="checkbox" checked={selectedIds.includes(pathway.id)} onChange={(e) => toggleSelect(pathway.id, e.target.checked)} />
                      <span className="text-gray-300">Select</span>
                    </label>
                  </div>
                  <PathwayCard
                    pathway={pathway}
                    onEdit={() => handleEdit(pathway)}
                    onDelete={() => handleDelete(pathway.id)}
                    onCreateDraft={() => handleCreateDraft(pathway)}
                    onManageVersions={() => handleOpenVersions(pathway)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pathway Versions Modal */}
      {versionsPathway && (
        <PathwayVersionsModal
          pathway={versionsPathway}
          onClose={() => setVersionsPathway(null)}
          onRefresh={() => {
            if (selectedCountry) {
              loadPathways(selectedCountry.id);
            }
          }}
        />
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="admin-modal">
          <div className="admin-modal-content bg-gray-800 text-white border border-gray-700">
            <PathwayForm
              pathway={editingPathway}
              country={selectedCountry}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingPathway(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PathwayManager;
